/**
 * AP-13 — Portierung von `src/license/LicenseManager.cpp`.
 *
 * Gleiches Protokoll, gleiche Signaturprüfung, gleiche Fristen. Bewusst
 * abweichend (MIGRATION_PLAN.md §7):
 *
 * - `ComputeHardwareId()` (Volume-Serial + Rechnername) hat im Browser keine
 *   Entsprechung. Statt dessen wird bei der ersten Ausführung eine zufällige
 *   **Installations-ID** erzeugt und in IndexedDB abgelegt; sie geht als
 *   `hardware_id` an den Server und ist damit Teil der signierten Nutzlast.
 *   Der Schutzgrad sinkt (die ID lässt sich mitkopieren), die Bindung
 *   „ein Aktivierungsplatz je Installation" bleibt.
 * - Die Uhr-Rückstell-Erkennung (`ClockRollbackDetected()`) entfällt: im
 *   Browser ist der Zustand ohnehin löschbar, ein zusätzlicher
 *   `lastSeenUtc`-Hochwasserstand kauft dagegen nichts.
 * - Die Signatur wird nicht bei *jeder* Abfrage neu geprüft (Web Crypto ist
 *   asynchron), sondern immer dann, wenn sich der Zustand ändert
 *   (`load`/`activate`/`validate`). `isCurrentlyValid()` bleibt dadurch
 *   synchron und für die UI-Schleife billig.
 */

import { importLicensePublicKey, verifyLicenseSignature, LICENSE_RSA_MODULUS_HEX } from './publicKey';
import { createIndexedDbLicenseStorage, type LicenseStorage } from './storage';

export interface LicenseState {
  serverUrl: string;
  licenseKey: string;
  /** Ersetzt `hardwareId` des Originals — siehe Dateikommentar. */
  installationId: string;
  activationToken: string;
  /** Hex-RSA-Signatur über `key|installationId|expiryUtc`. */
  signature: string;
  expiryUtc: string;
  lastValidationUtc: string;
  firstRunUtc: string;
  activated: boolean;
}

/** `LicenseManager::kOfflineGraceDays`. */
export const OFFLINE_GRACE_DAYS = 30;
/** `LicenseManager::kTrialGraceMinutes` — ein Tag ohne Lizenz je Installation. */
export const TRIAL_GRACE_MINUTES = 1440;
/** `LicenseWindow.cpp::kLicenseServerUrl` — der einzige Server, mit dem geredet wird. */
export const LICENSE_SERVER_URL = 'https://license.ma2p.me';

export interface LicenseManagerOptions {
  storage?: LicenseStorage;
  serverUrl?: string;
  /** Nur für Tests/lokale Server: anderer Signaturschlüssel. */
  modulusHex?: string;
  now?: () => number;
  fetchImpl?: typeof fetch;
}

function emptyState(): LicenseState {
  return {
    serverUrl: '',
    licenseKey: '',
    installationId: '',
    activationToken: '',
    signature: '',
    expiryUtc: '',
    lastValidationUtc: '',
    firstRunUtc: '',
    activated: false,
  };
}

/** Format wie `ComputeHardwareId()`: 16 Großbuchstaben-Hexziffern. */
function newInstallationId(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** `NowIso8601Utc()` — ohne Millisekunden, der Server parst `%Y-%m-%dT%H:%M:%SZ`. */
export function toIso8601Utc(millis: number): string {
  return `${new Date(millis).toISOString().slice(0, 19)}Z`;
}

/** `ParseIso8601Utc()`; `null` statt `-1` bei unlesbarem Zeitstempel. */
export function parseIso8601Utc(value: string): number | null {
  if (value === '') return null;
  const millis = Date.parse(value);
  return Number.isNaN(millis) ? null : millis;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  return typeof value === 'string' ? value : '';
}

export class LicenseManager {
  private state_: LicenseState = emptyState();
  private loaded = false;
  private signatureValid = false;
  private publicKey: Promise<CryptoKey> | null = null;

  private readonly storage: LicenseStorage;
  private readonly serverUrl: string;
  private readonly modulusHex: string;
  private readonly now: () => number;
  private readonly fetchImpl: typeof fetch;

  constructor(options: LicenseManagerOptions = {}) {
    this.storage = options.storage ?? createIndexedDbLicenseStorage();
    this.serverUrl = options.serverUrl ?? LICENSE_SERVER_URL;
    this.modulusHex = options.modulusHex ?? LICENSE_RSA_MODULUS_HEX;
    this.now = options.now ?? (() => Date.now());
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
  }

  get state(): Readonly<LicenseState> {
    return this.state_;
  }

  /**
   * `LicenseManager::Load()`. Stempelt Installations-ID und `firstRunUtc`
   * genau einmal — der Trial-Zähler startet also beim ersten App-Start, nicht
   * erst beim Öffnen des Lizenzfensters.
   */
  async load(): Promise<LicenseState> {
    if (this.loaded) return this.state_;
    let stored: LicenseState | null = null;
    try {
      stored = await this.storage.read();
    } catch {
      stored = null; // z. B. IndexedDB im Privatmodus blockiert
    }
    this.state_ = { ...emptyState(), ...(stored ?? {}) };
    this.loaded = true;

    let dirty = false;
    if (this.state_.installationId === '') {
      this.state_.installationId = newInstallationId();
      dirty = true;
    }
    if (this.state_.firstRunUtc === '') {
      this.state_.firstRunUtc = toIso8601Utc(this.now());
      dirty = true;
    }
    if (dirty) await this.save();

    await this.refreshSignatureCheck();
    return this.state_;
  }

  private async save(): Promise<void> {
    try {
      await this.storage.write(this.state_);
    } catch {
      // Ein nicht schreibbarer Speicher darf die App nicht anhalten; der
      // Zustand gilt dann nur für diese Sitzung.
    }
  }

  private getPublicKey(): Promise<CryptoKey> {
    this.publicKey ??= importLicensePublicKey(this.modulusHex);
    return this.publicKey;
  }

  /**
   * Prüft die gespeicherte Signatur gegen den *aktuellen* Zustand. Weil
   * `key|installationId|expiry` signiert ist, fällt jede Manipulation an
   * Schlüssel, Installations-ID oder Ablaufdatum hier auf.
   */
  private async refreshSignatureCheck(): Promise<void> {
    if (!this.state_.activated || this.state_.signature === '') {
      this.signatureValid = false;
      return;
    }
    const payload = `${this.state_.licenseKey}|${this.state_.installationId}|${this.state_.expiryUtc}`;
    try {
      this.signatureValid = await verifyLicenseSignature(await this.getPublicKey(), payload, this.state_.signature);
    } catch {
      this.signatureValid = false;
    }
  }

  private async postJson(path: string, body: unknown): Promise<{ data: Record<string, unknown>; error: string | null }> {
    let response: Response;
    try {
      response = await this.fetchImpl(`${this.serverUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        // Keine Cookies/Anmeldedaten an den Lizenzserver, und der Service
        // Worker soll Lizenzantworten niemals ausliefern (MIGRATION_PLAN §6).
        credentials: 'omit',
        cache: 'no-store',
      });
    } catch {
      return { data: {}, error: 'Request failed (license server unreachable)' };
    }

    let data: Record<string, unknown> = {};
    try {
      const parsed: unknown = await response.json();
      if (isRecord(parsed)) data = parsed;
    } catch {
      data = {};
    }
    if (!response.ok) {
      const serverError = readString(data, 'error');
      return { data, error: serverError !== '' ? serverError : `Server returned HTTP ${response.status}` };
    }
    return { data, error: null };
  }

  /** `LicenseManager::Activate()`; Rückgabe: Fehlertext oder `null` bei Erfolg. */
  async activate(key: string): Promise<string | null> {
    await this.load();
    const installationId = this.state_.installationId;
    const { data, error } = await this.postJson('/activate', { key, hardware_id: installationId });
    if (error !== null) return error;
    if (data.success !== true) {
      const serverError = readString(data, 'error');
      return serverError !== '' ? serverError : 'Activation rejected by server';
    }

    const expiry = readString(data, 'expiry');
    const signature = readString(data, 'signature');
    const verified = await verifyLicenseSignature(await this.getPublicKey(), `${key}|${installationId}|${expiry}`, signature);
    if (!verified) {
      return (
        'Server response signature invalid -- possible tampering, or the embedded public key ' +
        "doesn't match this server's signing key"
      );
    }

    this.state_ = {
      ...this.state_,
      serverUrl: this.serverUrl,
      licenseKey: key,
      activationToken: readString(data, 'token'),
      signature,
      expiryUtc: expiry,
      lastValidationUtc: toIso8601Utc(this.now()),
      activated: true,
    };
    this.signatureValid = true;
    await this.save();
    return null;
  }

  /** `LicenseManager::Validate()`. */
  async validate(): Promise<string | null> {
    await this.load();
    if (!this.state_.activated || this.state_.licenseKey === '') return 'Not activated yet';

    const { data, error } = await this.postJson('/validate', {
      key: this.state_.licenseKey,
      hardware_id: this.state_.installationId,
      token: this.state_.activationToken,
    });
    // Netzfehler ändern den Zustand bewusst NICHT — darüber entscheidet allein
    // die Offline-Gnadenfrist in `isCurrentlyValid()`.
    if (error !== null) return error;

    if (data.success !== true) {
      const serverError = readString(data, 'error');
      this.state_ = { ...this.state_, activated: false };
      this.signatureValid = false;
      await this.save();
      return serverError !== '' ? serverError : 'Server rejected this license (revoked/expired?)';
    }

    const newExpiry = readString(data, 'expiry');
    const effectiveExpiry = newExpiry === '' ? this.state_.expiryUtc : newExpiry;
    const signature = readString(data, 'signature');
    const payload = `${this.state_.licenseKey}|${this.state_.installationId}|${effectiveExpiry}`;
    if (!(await verifyLicenseSignature(await this.getPublicKey(), payload, signature))) {
      return 'Server response signature invalid -- possible tampering';
    }

    this.state_ = {
      ...this.state_,
      expiryUtc: effectiveExpiry,
      signature,
      lastValidationUtc: toIso8601Utc(this.now()),
    };
    this.signatureValid = true;
    await this.save();
    return null;
  }

  /** `LicenseManager::IsCurrentlyValid()` — rein lokal, ohne Netz. */
  isCurrentlyValid(): boolean {
    if (!this.state_.activated || this.state_.licenseKey === '') return false;
    if (!this.signatureValid) return false;
    const expiry = parseIso8601Utc(this.state_.expiryUtc);
    if (expiry !== null && this.now() > expiry) return false;
    return this.daysSinceLastValidation() <= OFFLINE_GRACE_DAYS;
  }

  /** `LicenseManager::DaysSinceLastValidation()`. */
  daysSinceLastValidation(): number {
    const last = parseIso8601Utc(this.state_.lastValidationUtc);
    if (last === null) return OFFLINE_GRACE_DAYS + 1;
    return Math.floor((this.now() - last) / 86_400_000);
  }

  /** `LicenseManager::MinutesSinceFirstRun()`. */
  minutesSinceFirstRun(): number {
    const first = parseIso8601Utc(this.state_.firstRunUtc);
    if (first === null) return TRIAL_GRACE_MINUTES + 1;
    return Math.floor((this.now() - first) / 60_000);
  }

  /** `LicenseManager::IsTrialActive()`. */
  isTrialActive(): boolean {
    return this.minutesSinceFirstRun() < TRIAL_GRACE_MINUTES;
  }

  /** `LicenseManager::TrialSecondsRemaining()` — Grundlage des HH:MM:SS-Countdowns. */
  trialSecondsRemaining(): number {
    const first = parseIso8601Utc(this.state_.firstRunUtc);
    if (first === null) return 0;
    const remaining = Math.floor(TRIAL_GRACE_MINUTES * 60 - (this.now() - first) / 1000);
    return remaining > 0 ? remaining : 0;
  }

  /** `LicenseManager::IsUsageAllowed()` — hierauf sperrt die App Funktionen. */
  isUsageAllowed(): boolean {
    return this.isCurrentlyValid() || this.isTrialActive();
  }

  /** Wortlaut der Statuszeile aus `LicenseWindow::RefreshStatusText()`. */
  statusText(): string {
    if (!this.state_.activated) return 'Not activated';
    if (this.isCurrentlyValid()) return 'Active';
    return this.state_.expiryUtc === '' ? 'Unknown' : 'Expired / grace period exceeded';
  }
}
